import { useState } from 'react';
import { Message } from '../types';
import { useAuthStore } from '../store';

interface MessageActionsProps {
  message: Message;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onEdit: (messageId: string, newContent: string) => void;
  onDelete: (messageId: string) => void;
  onRegenerate: (messageId: string) => void;
  onInspect: (messageId: string) => void;
  /** Start a new world from the story at this message; absent where stories cannot branch. */
  onBranch?: (messageId: string) => Promise<void>;
  isStreaming: boolean;
  canRegenerate: boolean;
}

export function MessageActions({
  message,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onEdit,
  onDelete,
  onRegenerate,
  onInspect,
  onBranch,
  isStreaming,
  canRegenerate,
}: MessageActionsProps) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);
  const [branching, setBranching] = useState(false);
  const [branchError, setBranchError] = useState<string | null>(null);
  const user = useAuthStore(state => state.user);
  const isAdmin = !!user?.is_admin;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setShowCopyFeedback(true);
      setTimeout(() => setShowCopyFeedback(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleBranch = async () => {
    if (!onBranch) return;
    setBranching(true);
    setBranchError(null);
    try {
      await onBranch(message.id);
    } catch (err) {
      setBranchError(err instanceof Error ? err.message : 'Could not branch this story');
    } finally {
      setBranching(false);
    }
  };

  const handleDelete = () => {
    onDelete(message.id);
    setShowConfirmDelete(false);
  };

  // Show editing controls when in edit mode
  if (isEditing) {
    return (
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs text-dark-400">Editing...</span>
        <button
          onClick={onCancelEdit}
          className="px-2 py-1 text-xs bg-dark-600 hover:bg-dark-500 text-dark-200 rounded"
        >
          Cancel
        </button>
        <span className="text-xs text-dark-500">Press Enter to save</span>
      </div>
    );
  }

  if (showConfirmDelete) {
    return (
      <div className="mt-2 p-2 bg-red-900/30 border border-red-700 rounded">
        <p className="text-red-200 text-xs mb-2">Delete this message?</p>
        <div className="flex gap-2">
          <button
            onClick={handleDelete}
            className="px-3 py-1 text-xs bg-red-600 hover:bg-red-500 text-white rounded"
          >
            Delete
          </button>
          <button
            onClick={() => setShowConfirmDelete(false)}
            className="px-3 py-1 text-xs bg-dark-600 hover:bg-dark-500 text-dark-200 rounded"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity md:pointer-events-none md:group-hover:pointer-events-auto">
      {/* Copy - available for all messages */}
      <button
        onClick={handleCopy}
        className="p-1 text-dark-300 hover:text-dark-100"
        title="Copy to clipboard"
      >
        {showCopyFeedback ? (
          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>

      {/* Edit - available for ALL messages */}
      <button
        onClick={onStartEdit}
        disabled={isStreaming}
        className="p-1 text-dark-300 hover:text-dark-100 disabled:opacity-50"
        title="Edit message"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </button>

      {/* Retry/Resubmit - available for user messages only */}
      {canRegenerate && message.role === 'user' && (
        <button
          onClick={() => onRegenerate(message.id)}
          disabled={isStreaming}
          className="p-1 text-dark-300 hover:text-green-400 disabled:opacity-50"
          title="Resubmit message"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      )}

      {/* Regenerate - available for assistant messages only */}
      {canRegenerate && message.role === 'assistant' && (
        <button
          onClick={() => onRegenerate(message.id)}
          disabled={isStreaming}
          className="p-1 text-dark-300 hover:text-dark-100 disabled:opacity-50"
          title="Regenerate response"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      )}

      {/* Branch - a new world from this point; an input is handed back to send again */}
      {onBranch && (
        <button
          onClick={handleBranch}
          disabled={isStreaming || branching}
          className={`p-1 text-dark-300 hover:text-dark-100 disabled:opacity-50 ${branching ? 'animate-pulse' : ''}`}
          title={message.role === 'user' ? 'Branch from here: a new world where you can send this again or change it' : 'Branch from here: a new world that continues from this point'}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <line x1="6" y1="3" x2="6" y2="15" />
            <circle cx="18" cy="6" r="3" />
            <circle cx="6" cy="18" r="3" />
            <path d="M18 9a9 9 0 0 1-9 9" />
          </svg>
        </button>
      )}
      {branchError && <span className="text-xs text-red-400">{branchError}</span>}

      {/* Spacer to push delete and inspect to far right */}
      <div className="flex-1" />

      {/* Delete - available for all messages (pushed to far right) */}
      <button
        onClick={() => setShowConfirmDelete(true)}
        disabled={isStreaming}
        className="p-1 text-dark-300 hover:text-red-400 disabled:opacity-50"
        title="Delete message"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>

      {/* Inspect - admin only, assistant messages only */}
      {isAdmin && message.role === 'assistant' && message.inference_log_id && (
        <button
          onClick={() => onInspect(message.id)}
          className="p-1 text-primary-400 hover:text-primary-300"
          title="Inspect LLM call (Admin)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      )}
    </div>
  );
}
